import { useEffect } from "react";
import { useLocale, useT } from "@/lib/i18n";

import { SITE_URL as SITE, SITE_INDEXABLE } from "@/lib/site";
import { pathForLocale, stripLocale } from "@/lib/locale-url";
const SUFFIX = "Glastotaal";
/**
 * Google kapt de titel in de zoekresultaten af rond de 60 tekens. De merknaam achteraan is
 * daarvan het minst informatieve deel — die laten we dus weg zodra hij de titel over die grens
 * zou duwen, in plaats van de titel zelf te verminken. Zo blijft de kern altijd zichtbaar.
 */
const TITLE_MAX = 60;
export function withBrandSuffix(title: string): string {
  if (title === SUFFIX) return title;
  // Titels die het merk zelf al noemen ("Herroepingsrecht — Glastotaal") niet nogmaals
  // achtervoegen: dat gaf "… — Glastotaal | Glastotaal" in 16 zoekresultaten.
  if (title.toLowerCase().includes(SUFFIX.toLowerCase())) return title;
  return title.length + 3 + SUFFIX.length <= TITLE_MAX ? `${title} | ${SUFFIX}` : title;
}
/** Standaard social-preview: 1200×630 (de verhouding die Facebook/LinkedIn/X én Google's
 *  zoekresultaten willen), uitgesneden uit de hero-foto. Was het logo, maar dat wordt in die
 *  liggende kaart lelijk bijgesneden en zegt niets over de pagina. Pagina's met een eigen,
 *  voldoende grote foto horen 'm te overschrijven — zie `image` hieronder. */
const DEFAULT_OG_IMAGE = `${SITE}/social-card.webp`;

interface Props {
  /** Page title (without the brand suffix — that's appended). */
  title: string;
  description?: string;
  /** Path for the canonical URL, e.g. "/over-ons". Defaults to current path. */
  canonicalPath?: string;
  /** Optional JSON-LD object(s) to inject as <script type="application/ld+json">. */
  jsonLd?: object | object[];
  /** Keep transactional pages (cart, checkout) out of the index. */
  noindex?: boolean;
  /** Absolute of pad-URL voor de og:image/twitter:image (default = hero-foto). */
  image?: string;
  /**
   * Deze pagina bestaat maar in één taal: de canonical wijst altijd naar het Nederlandse pad en
   * er komen géén hreflang-verwijzingen.
   *
   * Voor de artikelpagina's. Die staan bewust niet in de sitemap (het zijn ~2.400 machinaal
   * vertaalde onderdeelnamen — voor Google dunne, duplicaat-achtige content), maar Google vindt
   * ze wél: elke link op een /en-pagina krijgt via de router-basename automatisch het /en-prefix.
   * Ze canonicaliseerden zichzélf en werden dus alsnog als losse pagina's aangeboden. Resultaat
   * in Search Console: /en/artikel/… in "Gecrawld – momenteel niet geïndexeerd", terwijl 167
   * échte Nederlandse artikelpagina's onder "Gevonden – momenteel niet geïndexeerd" op crawl-
   * budget stonden te wachten. De Engelse variant blijft gewoon werken voor bezoekers; hij vraagt
   * alleen geen eigen plek in de index meer.
   */
  dutchOnly?: boolean;
}

function upsertMeta(attr: "name" | "property", key: string, content: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertCanonical(href: string): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

/**
 * Lightweight, dependency-free SEO head manager. Sets <title>, meta description,
 * canonical and optional JSON-LD on mount/update. Prerender-friendly: a crawler
 * that runs the page (or a future RR prerender) captures the resulting <head>.
 */
export function Seo({ title, description, canonicalPath, jsonLd, noindex, image, dutchOnly }: Props) {
  const [locale] = useLocale();
  const t = useT();
  useEffect(() => {
    // Vertaal de paginatitel (tab/OG) via de i18n-dict; no-op als er geen EN-sleutel is, dus
    // dynamische/al-vertaalde titels blijven ongemoeid. Vangt hardcoded NL-titels (Winkelwagen,
    // Afrekenen, …) af zodat de browser-tab in EN-modus ook Engels is.
    const translated = t(title);
    const fullTitle = withBrandSuffix(translated);
    document.title = fullTitle;

    const created: Element[] = [];
    // Draait de build niet op het echte domein (testomgeving/preview), dan ALTIJD noindex —
    // zo kan staging nooit met de eigen site concurreren. Zie src/lib/site.ts.
    upsertMeta("name", "robots", noindex || !SITE_INDEXABLE ? "noindex, follow" : "index, follow");
    // Ook de omschrijving door de dict: die stond tot 01-08-2026 rauw in de meta, waardoor
    // Engelse pagina's een Nederlandse zoekresultaat-tekst kregen (gevonden met de sitemap-audit).
    // Geen sleutel = ongewijzigd, dus samengestelde omschrijvingen blijven ongemoeid.
    if (description) {
      const vertaald = t(description);
      upsertMeta("name", "description", vertaald);
      upsertMeta("property", "og:description", vertaald);
      upsertMeta("name", "twitter:description", vertaald);
    }
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SUFFIX);
    upsertMeta("property", "og:locale", locale === "en" ? "en_US" : "nl_NL");

    const ogImage = image ? (image.startsWith("http") ? image : `${SITE}${image}`) : DEFAULT_OG_IMAGE;
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:image", ogImage);

    // Een expliciete canonicalPath ("/reviews") is taalloos: op de Engelse site moet daar het
    // /en-prefix voor, anders wijst de Engelse pagina zichzélf naar de Nederlandse versie en
    // indexeert Google 'm nooit. Zonder prop volgt de canonical gewoon het huidige pad.
    const path = dutchOnly
      ? stripLocale(canonicalPath ?? window.location.pathname)
      : canonicalPath
        ? pathForLocale(canonicalPath, locale)
        : window.location.pathname;
    const canonical = `${SITE}${path}`;
    upsertCanonical(canonical);
    upsertMeta("property", "og:url", canonical);

    // hreflang: vertel Google dat /pad en /en/pad dezelfde pagina in twee talen zijn. Zonder deze
    // verwijzingen ziet hij twee losse pagina's met bijna dezelfde inhoud (duplicaatrisico) en
    // toont hij bovendien de verkeerde taal aan de verkeerde bezoeker. x-default → Nederlands,
    // want dat is de hoofdmarkt. Alleen op INDEXEERBARE pagina's; noindex-pagina's krijgen niets.
    document.head.querySelectorAll('link[rel="alternate"][data-seo="1"]').forEach((el) => el.remove());
    if (!noindex && !dutchOnly && SITE_INDEXABLE) {
      const kaalPad = stripLocale(new URL(canonical).pathname);
      for (const [hreflang, pad] of [
        ["nl", kaalPad],
        ["en", pathForLocale(kaalPad, "en")],
        ["x-default", kaalPad],
      ] as const) {
        const alt = document.createElement("link");
        alt.setAttribute("rel", "alternate");
        alt.setAttribute("hreflang", hreflang);
        alt.setAttribute("href", `${SITE}${pad}`);
        alt.setAttribute("data-seo", "1");
        document.head.appendChild(alt);
        created.push(alt);
      }
    }

    // Verwijder eventueel vooraf-gerenderde (prerender) JSON-LD zodat JS-bezoekers
    // niet dubbele blokken krijgen; we injecteren ze hieronder vers.
    document.head.querySelectorAll('script[data-seo="1"]').forEach((el) => el.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const block of blocks) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo", "1");
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
        created.push(script);
      }
    }
    return () => {
      for (const el of created) el.remove();
    };
    // `image` en `noindex` MOETEN hierin staan: een pagina die zijn foto pas ná de eerste render
    // kent (categorieën wachten op de admin-overrides) hield anders eeuwig de standaardkaart.
  }, [title, description, canonicalPath, jsonLd, locale, t, image, noindex, dutchOnly]);

  return null;
}

export { SITE as SITE_URL };
